<template>
    <!--
        „Kam?“ — every screen this account may open, by typing three letters of its
        name. Ctrl+K anywhere, or the entry at the top of the menu.

        It is the answer to the one thing grouping a menu costs: somebody who knows
        where „Prejeti računi“ is now has to remember which of seven folds it is under.
        Typing it is faster than remembering, and faster than the flat list ever was.
    -->
    <UModal
        v-model:open="searchOpen"
        :title="$t('nav.search')"
        :description="$t('nav.search_hint')"
        :ui="{ content: 'max-w-2xl', body: 'p-0 sm:p-0' }"
    >
        <template #body>
            <UCommandPalette
                :groups="paletteGroups"
                :placeholder="$t('nav.search_placeholder')"
                :fuse="fuse"
                :ui="{ input: '[&>input]:h-12' }"
                class="h-96"
            >
                <template #item-trailing="{ item }">
                    <button
                        type="button"
                        class="flex size-6 items-center justify-center rounded text-dimmed hover:text-primary focus-visible:outline-2 focus-visible:outline-primary"
                        :title="starTitle(item)"
                        :aria-label="starTitle(item)"
                        :aria-pressed="isFavourite(String(item.id))"
                        @click.stop.prevent="toggleFavourite(String(item.id))"
                    >
                        <UIcon
                            name="i-lucide-star"
                            class="size-4"
                            :class="isFavourite(String(item.id)) ? 'fill-current text-primary' : ''"
                        />
                    </button>
                </template>

                <template #empty>
                    <div class="p-6 text-center text-sm text-muted">{{ $t('nav.search_empty') }}</div>
                </template>
            </UCommandPalette>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import { foldAccents, useNav } from '@/libs/nav'
    import type { INavGroup, INavItem } from '@/types/Nav'

    /** One row of the palette: a nav entry, plus what Fuse searches it by. */
    interface IPaletteItem {
        id:          string
        label:       string
        suffix:      string
        icon:        string
        /** The label with its accents folded away — see `fuse` below. */
        searchLabel: string
        onSelect:    () => void
    }

    const { t }    = useI18n()
    const router   = useRouter()

    // The shared menu, the same object the sidebar draws from.
    // `searchGroups` and not `groups`: the sidebar folds each tab group into ONE entry,
    // and the palette is the place that must not. Somebody typing „napoved“ is asking
    // for a screen, and having to know it became the fourth tab of „Knjigovodstvo
    // materiala“ is the very thing the palette exists to spare them.
    const {
        searchGroups, settingsGroups, searchOpen, closeSearch,
        isFavourite, toggleFavourite, tabGroupLabel,
    } = useNav()

    // ⌘K and Ctrl+K, and it OPENS rather than toggles.
    //
    // `meta` is rewritten to `ctrl` off macOS by the composable itself, so on Windows
    // and Linux both lines match the same press and the handler runs twice. A toggle
    // would open and shut in the same keystroke and the palette would never appear; an
    // open is an open however many times it is asked for. Shutting it is Escape, or the
    // modal's own close, which is what every palette does.
    //
    // Both fire while an input is focused: a person halfway through a filter who wants
    // another screen should not have to click out of the field first.
    defineShortcuts({
        meta_k: { usingInput: true, handler: () => { searchOpen.value = true } },
        ctrl_k: { usingInput: true, handler: () => { searchOpen.value = true } },
    })

    const go = (item: INavItem) => {
        closeSearch()

        if (item.external) {
            if (item.href) window.open(item.href, '_blank', 'noopener')

            return
        }

        void router.push({ name: item.name })
    }

    /**
     * Where the screen is, read left to right: the fold, then the tab group it became.
     *
     * The fold's own name is there because several entries read the same on their own —
     * „Pregled“, „Veriga“ and „Obroki“ each belong to two places — and the tab group is
     * there because „Prejeti računi“ is now a tab and the answer to "where is it?" is
     * „Finance › Knjigovodstvo materiala“.
     */
    const suffixOf = (item: INavItem, groupLabel: string): string => {
        const tabs = item.tabs ? tabGroupLabel(item.tabs) : ''

        return tabs ? `${groupLabel} › ${tabs}` : groupLabel
    }

    const toPaletteItem = (item: INavItem, groupLabel: string): IPaletteItem => {
        const suffix = suffixOf(item, groupLabel)

        return {
            id:          item.name,
            label:       item.label,
            suffix,
            icon:        item.icon,
            searchLabel: `${foldAccents(item.label)} ${foldAccents(suffix)}`,
            onSelect:    () => go(item),
        }
    }

    const toPaletteGroup = (group: INavGroup) => ({
        id:    group.key,
        label: t(group.labelKey),
        items: group.sections
            .flatMap(section => section.items)
            .map(item => toPaletteItem(item, t(group.labelKey))),
    })

    /** The working menu first, then what lives behind the gear. */
    const paletteGroups = computed(() =>
        [...searchGroups.value, ...settingsGroups.value].map(toPaletteGroup))

    /**
     * Searched without accents.
     *
     * „Prejeti računi“ has to be found by typing "racuni", because half of this desk
     * types on a keyboard whose č and š are two presses away and the other half does
     * not think about it at all. Fuse matches the string it is given, so each row
     * carries a folded copy of its own label and the keys name it.
     */
    const fuse = {
        fuseOptions: { ignoreLocation: true, threshold: 0.2, keys: ['label', 'suffix', 'searchLabel'] },
        resultLimit: 20,
    }

    const starTitle = (item: unknown): string =>
        isFavourite(String((item as IPaletteItem).id))
            ? t('nav.favourite_remove')
            : t('nav.favourite_add')
</script>
